import { Capacitor } from '@capacitor/core';
import { getPlatform, isNativeApp } from './platform';
import { ageGateAnswered, ageGateChildDeclared, parentVerified } from './ageGate';
import { getAnonStory } from './anonStory';
import { FIRST_RUN_DONE_KEY, FIRST_RUN_VISIT_KEY, firstRunVariant } from './firstRunGate';
import type { FirstRunVariant } from './metrics';

/**
 * Storage glue of the story-first native first run (2026-10-02, docs/NATIVE-FIRST-RUN-2026-10.md step 6); the rules
 * are in lib/firstRunCore.ts.
 *
 * The one-shot "done" flag is read synchronously from localStorage (LandingRoute decides in its render) and written to
 * localStorage AND @capacitor/preferences when the shell has the plugin — like lib/installId.ts: Preferences survives
 * an iOS WebView storage eviction, so App.tsx copies it back into localStorage at boot (restoreFirstRunDone) and
 * FirstRunPage waits for that copy before it decides.
 */
const DONE_KEY = FIRST_RUN_DONE_KEY;
const FIRST_OPEN_KEY = 'snugtale_first_open_v1';

export function firstRunDone(): boolean {
  try {
    return localStorage.getItem(DONE_KEY) === '1';
  } catch {
    return true;   // no storage = no once-per-install guarantee: the library as before
  }
}

function preferencesAvailable(): boolean {
  try {
    return isNativeApp() && Capacitor.isPluginAvailable('Preferences');
  } catch {
    return false;
  }
}

/**
 * The first run ended (a trial story, a sign-in, the library, an import, or — account variant — the waiting choice
 * consumed by HomePage after the sign-up): never again. Not at the account hand-off itself (2026-10-02 review): a back
 * from /register must find the loves step with the choice, not the library. Drops the visit's answers (sessionStorage,
 * components/firstRun/firstRunVisit.ts); a done install also no longer owes lib/nativeFirstTap.ts its first-tap beacon
 * (that module reads the same flag).
 */
export function markFirstRunDone(): void {
  try { localStorage.setItem(DONE_KEY, '1'); } catch { /* ignore */ }
  try { sessionStorage.removeItem(FIRST_RUN_VISIT_KEY); } catch { /* ignore */ }
  if (!preferencesAvailable()) return;
  import('@capacitor/preferences')
    .then(({ Preferences }) => Preferences.set({ key: DONE_KEY, value: '1' }))
    .catch(() => { /* plugin misbehaved — localStorage still has it */ });
}

let restorePromise: Promise<void> | null = null;

/** Boot: Preferences → localStorage, once per page load (App.tsx starts it, FirstRunPage awaits it). */
export function restoreFirstRunDone(): Promise<void> {
  if (!restorePromise) {
    restorePromise = !preferencesAvailable() || firstRunDone()
      ? Promise.resolve()
      : import('@capacitor/preferences')
        .then(async ({ Preferences }) => {
          const { value } = await Preferences.get({ key: DONE_KEY });
          if (value === '1') {
            try { localStorage.setItem(DONE_KEY, '1'); } catch { /* ignore */ }
          }
        })
        .catch(() => { /* plugin unavailable — localStorage decides */ });
  }
  return restorePromise;
}

function firstOpenAt(): number | null {
  try {
    const n = Number(localStorage.getItem(FIRST_OPEN_KEY) || 0);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/** The core rule with this launch's live values. */
export function liveFirstRunVariant(signedIn: boolean): FirstRunVariant | null {
  return firstRunVariant({
    native: isNativeApp(),
    signedIn,
    platform: getPlatform(),
    ageGateAnswered: ageGateAnswered(),
    childDeclared: ageGateChildDeclared(),
    parentVerified: parentVerified(),
    done: firstRunDone(),
    firstOpenAt: firstOpenAt(),
    now: Date.now(),
    hasTrialRecord: !!getAnonStory(),
  });
}
